import React, { useEffect, useRef, useState } from "react";
import Scroll from "react-scroll";
import { each, get, includes, set } from "lodash";
import {
    Button,
    ButtonBase,
    Checkbox,
    Chip,
    ClickAwayListener,
    Divider,
    Fade,
    FormControlLabel,
    Grid,
    Paper,
    Popper,
    Radio,
    Switch,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import BSInput from "../../../common/js/components/BSInput";
import BSSelect from "../../../common/js/components/BSSelect";
import { DownChevron } from "../../../common/js/icons";
import LocationFilters from "../property/LocationFilters";
import MarketFilters from "../property/MarketFilters";
import PropertyTypeFilter from "../property/PropertyTypeFilter";

import searchConstants from "../search/constants";

const MenuSearchButton = styled(Button)({
    marginBottom: "15px",
});

const PropertyTypeContainer = styled(Paper)({
    width: "32em",
});

const MarketFilterContainer = styled(Paper)({
    width: "32em",
});

const StatusContainer = styled("div")({
    minWidth: "10vw",
    padding: "0 20px",
    background: "white  ",
});

const FakeSelect = styled(ButtonBase)({
    height: "39px",
    border: "1px solid #E0E0E0",
    borderRadius: "4px",
    marginLeft: 0,
    fontWeight: 400,
    width: "100%",
    justifyContent: "flex-start",
    padding: "0 26px 0 6px",
    backgroundColor: "white",
    fontSize: "14px",
    "&.Mui-focusVisible": {
        border: "1px solid #2C99D2",
        backgroundColor: "white",
    },
});

const FocusVisible = styled("div")({
    border: "1px solid #2C99D2",
    backgroundColor: "white",
});

const FakeSelectIcon = styled("span")({
    position: "absolute",
    right: "6px",
    fontSize: "16px",
});

const FilterBar = styled("div")({
    backgroundColor: "white",
    paddingTop: "15px",
    paddingRight: "20px",
    paddingLeft: "15px",
    borderBottom: "1px solid #eee",
});

const StatusSwitch = styled(Switch)({
    marginRight: "-15px",
});

const MenuTitle = styled("div")({
    fontSize: "14px",
});

const StyledDivider = styled(Divider)({
    position: "absolute",
    width: "100%",
    left: 0,
    top: "40px",
});

const LocationFilterContainer = styled(Paper)({
    minWidth: "350px",
    padding: "20px",
});

const ListingTypeFilterContainer = styled(Paper)({
    minWidth: "550px",
    padding: "20px",
});

const MenuSectionTitle = styled("h4")({
    color: "#9E9E9E",
    fontSize: "13px",
    fontWeight: 600,
    textTransform: "uppercase",
    marginBottom: "5px",
});

const OfficeDropdown = styled(Grid)({
    marginTop: 0,
    height: "30px",
});

const StyledFilterContainer = styled("div")({
    display: "flex",
    gap: "1vw",
    justifyContent: "space-between",
    alignItems: "center",
});

const StyledFilterDropdownMenu = styled("div")({
    flex: "1 1 0",
});

const StyledStatusFilterContainer = styled("div")({
    display: "flex",
    justifyContent: "space-between",
    gap: "1vw",
});

const ListingFilters = (props) => {
    const {
        clearFilters,
        handleDeleteCrumb,
        handleSearchCleared,
        handleSelectAllPropertyType,
        handleSelectMarket,
        handleSelectMicromarket,
        handleSelectSubmarket,
        handleSizeRadioClick,
        loadedSearchCrumbs,
        marketFilterExpansionPanels,
        propertySearchFilters,
        propertyTypeExpansionPanels,
        role,
        searchCriteria,
        searchSubmit,
        toggleMarketFilterExpansionPanel,
        togglePropertyTypeExpansionPanel,
        updateFilter,
    } = props;

    const clickedOnDropdownToggleRef = useRef(false);
    const [openDropdown, setOpenDropdown] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [placement, setPlacement] = useState(null);

    useEffect(() => {
        initializeSizeCriteria();
    });

    const initializeSizeCriteria = () => {
        if (
            !get(searchCriteria, "sizeCriteria.availableSf", false) &&
            !get(searchCriteria, "sizeCriteria.totalSf", false) &&
            !get(searchCriteria, "sizeCriteria.acreage", false)
        ) {
            handleSizeRadioClick("availableSf", true);
        }
    };

    const handleClick = (placementArg) => (event, filter) => {
        clickedOnDropdownToggleRef.current = true;
        const { currentTarget } = event;
        if (openDropdown === filter && placement === placementArg) {
            // Toggle off the same dropdown
            setOpenDropdown(null);
            setAnchorEl(null);
        } else {
            // Open new dropdown
            setOpenDropdown(filter);
            setAnchorEl(currentTarget);
            setPlacement(placementArg);
        }
    };

    const handleClose = () => {
        if (clickedOnDropdownToggleRef.current) {
            clickedOnDropdownToggleRef.current = false; // Reset it
            return; // Skip closing — click was on dropdown toggle
        }
        setOpenDropdown(null);
    };

    const onKeyPress = (event) => {
        if (event.key === "Enter") {
            clickedOnDropdownToggleRef.current = false;
            searchSubmit();
            handleClose();
        }
    };

    const handleClearFilters = () => {
        clearFilters();
        handleSearchCleared(true);
        handleClose();
        handleSizeRadioClick("availableSf", true);
    };

    const handleSearchSubmit = () => {
        clickedOnDropdownToggleRef.current = false;
        handleSearchCleared(false);
        searchSubmit();
        handleClose();
    };

    const countPropertyTypeSelected = (searchCriteria) => {
        let count = 0;
        each(get(searchCriteria, "propertyTypes", []), (p) => {
            if (p.selected === true) {
                count += 1;
            }
        });
        return count;
    };

    const countFilter = (searchCriteria, filterName) => {
        let count = 0;
        each(get(searchCriteria, filterName, []), (option) => {
            if (option === true) {
                count += 1;
            }
        });
        return count;
    };

    const countFiltersSelected = (searchCriteria, filterName) => {
        let count = 0;
        each(get(searchCriteria, filterName, []), (option) => {
            if (option.selected === true) {
                count += 1;
            }
        });
        return count;
    };

    const checkForLeaseOnly = (checked, box) => {
        let currentState = {
            excludeSublease: get(searchCriteria, "listingTypeCriteria.excludeSubleases"),
            forSublease: get(searchCriteria, "listingTypeCriteria.forSublease"),
            investmentSaleFlg: get(searchCriteria, "listingTypeCriteria.investmentSaleFlg"),
            forSale: get(searchCriteria, "listingTypeCriteria.forSale"),
            forLease: get(searchCriteria, "listingTypeCriteria.forLease"),
        };
        set(currentState, box, checked);
        if (
            !get(currentState, "forSublease") &&
            !get(currentState, "investmentSaleFlg") &&
            !get(currentState, "forSale") &&
            get(currentState, "forLease") &&
            get(currentState, "excludeSubleases")
        ) {
            handleSelectAllPropertyType("Hospitality", false);
        }
        updateFilter(`listingTypeCriteria.${box}`, checked);
    };

    const combineFilterCount = (searchCriteria, criteriaOne, criteriaTwo) => {
        return countFilter(searchCriteria, criteriaOne) + countFilter(searchCriteria, criteriaTwo);
    };

    let Element = Scroll.Element;
    const canViewExpiredOnly = includes(
        [
            "dataAdmin",
            "dataEntry",
            "research",
            "officeManager",
            "researchAdmin",
            "portlandBrokerAdmin",
            "arizonaBrokerAdmin",
            "evp",
        ],
        role
    );

    return (
        <FilterBar>
            <StyledFilterContainer>
                <div style={{ fontWeight: 400, maxWidth: "70px", marginLeft: "12px", marginRight: "5px" }}>
                    Filters:
                </div>

                <StyledFilterDropdownMenu>
                    <Popper open={openDropdown === "location"} anchorEl={anchorEl} placement={placement} transition>
                        {({ TransitionProps }) => (
                            <Fade {...TransitionProps} timeout={100}>
                                <div>
                                    <ClickAwayListener onClickAway={handleClose}>
                                        <LocationFilterContainer>
                                            <LocationFilters
                                                updateFilter={updateFilter}
                                                searchCriteria={searchCriteria}
                                                onKeyPress={onKeyPress}
                                                hideFilters={true}
                                            ></LocationFilters>

                                            <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                                <div>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        onClick={handleSearchSubmit}
                                                    >
                                                        Search
                                                    </Button>
                                                </div>
                                            </div>
                                        </LocationFilterContainer>
                                    </ClickAwayListener>
                                </div>
                            </Fade>
                        )}
                    </Popper>
                    <FakeSelect
                        onClick={(e) => handleClick("bottom-start")(e, "location")}
                        focusVisibleClassName={FocusVisible}
                    >
                        {"Location"}
                        <FakeSelectIcon>
                            <DownChevron />
                        </FakeSelectIcon>
                    </FakeSelect>
                </StyledFilterDropdownMenu>

                <StyledFilterDropdownMenu>
                    <Popper open={openDropdown === "marketArea"} anchorEl={anchorEl} placement={placement} transition>
                        {({ TransitionProps }) => (
                            <Fade {...TransitionProps} timeout={100}>
                                <div>
                                    <ClickAwayListener onClickAway={handleClose}>
                                        <MarketFilterContainer>
                                            <Element style={{ height: "400px", overflowY: "scroll" }}>
                                                <MarketFilters
                                                    searchCriteria={searchCriteria}
                                                    toggleMarketFilterExpansionPanel={toggleMarketFilterExpansionPanel}
                                                    marketFilterExpansionPanels={marketFilterExpansionPanels}
                                                    handleSelectMarket={handleSelectMarket}
                                                    handleSelectSubmarket={handleSelectSubmarket}
                                                    handleSelectMicromarket={handleSelectMicromarket}
                                                ></MarketFilters>
                                            </Element>
                                            <Grid
                                                container
                                                spacing={16}
                                                justifyContent="flex-end"
                                                style={{ padding: "10px", paddingRight: "16px" }}
                                            >
                                                <Grid item>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        onClick={handleSearchSubmit}
                                                    >
                                                        Search
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </MarketFilterContainer>
                                    </ClickAwayListener>
                                </div>
                            </Fade>
                        )}
                    </Popper>
                    <FakeSelect
                        onClick={(e) => handleClick("bottom-start")(e, "marketArea")}
                        focusVisibleClassName={FocusVisible}
                    >
                        {`Market Area ${
                            countFiltersSelected(searchCriteria, "locationCriteria.markets") > 0
                                ? `(${countFiltersSelected(searchCriteria, "locationCriteria.markets")})`
                                : ""
                        }`}
                        <FakeSelectIcon>
                            <DownChevron />
                        </FakeSelectIcon>
                    </FakeSelect>
                </StyledFilterDropdownMenu>

                <StyledFilterDropdownMenu>
                    <Popper open={openDropdown === "propertyType"} anchorEl={anchorEl} placement={placement} transition>
                        {({ TransitionProps }) => (
                            <Fade {...TransitionProps} timeout={100}>
                                <div>
                                    <ClickAwayListener onClickAway={handleClose}>
                                        <PropertyTypeContainer>
                                            <PropertyTypeFilter
                                                updateFilter={updateFilter}
                                                propertySearchFilters={propertySearchFilters}
                                                handleSelectAllPropertyType={handleSelectAllPropertyType}
                                                searchCriteria={searchCriteria}
                                                handleSearchSubmit={handleSearchSubmit}
                                                togglePropertyTypeExpansionPanel={togglePropertyTypeExpansionPanel}
                                                propertyTypeExpansionPanels={propertyTypeExpansionPanels}
                                            />
                                        </PropertyTypeContainer>
                                    </ClickAwayListener>
                                </div>
                            </Fade>
                        )}
                    </Popper>
                    <FakeSelect
                        onClick={(e) => handleClick("bottom-start")(e, "propertyType")}
                        focusVisibleClassName={FocusVisible}
                    >
                        {`Space Use ${
                            countPropertyTypeSelected(searchCriteria) > 0
                                ? `(${countPropertyTypeSelected(searchCriteria)})`
                                : ""
                        }`}
                        <FakeSelectIcon>
                            <DownChevron />
                        </FakeSelectIcon>
                    </FakeSelect>
                </StyledFilterDropdownMenu>

                <StyledFilterDropdownMenu>
                    <Popper open={openDropdown === "dealType"} anchorEl={anchorEl} placement={placement} transition>
                        {({ TransitionProps }) => (
                            <Fade {...TransitionProps} timeout={100}>
                                <div>
                                    <ClickAwayListener onClickAway={handleClose}>
                                        <ListingTypeFilterContainer>
                                            <Element
                                                style={{
                                                    height: "400px",
                                                    paddingLeft: "5px",
                                                    paddingRight: "40px",
                                                    overflowX: "hidden",
                                                    overflowY: "scroll",
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        alignItems: "center",
                                                    }}
                                                >
                                                    <MenuTitle>KM Listings Only</MenuTitle>
                                                    <div>
                                                        <StatusSwitch
                                                            checked={get(
                                                                searchCriteria,
                                                                "listingTypeCriteria.kmListingsOnly",
                                                                false
                                                            )}
                                                            onChange={(e) =>
                                                                updateFilter(
                                                                    "listingTypeCriteria.kmListingsOnly",
                                                                    e.target.checked
                                                                )
                                                            }
                                                            value="kmListingsOnly"
                                                            color="primary"
                                                        />
                                                    </div>
                                                </div>

                                                <Divider style={{ margin: "4px -20px 10px -20px" }} />

                                                <div
                                                    style={{
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                    }}
                                                >
                                                    <div>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={get(
                                                                        searchCriteria,
                                                                        "listingTypeCriteria.forLease",
                                                                        false
                                                                    )}
                                                                    onChange={(e) =>
                                                                        checkForLeaseOnly(e.target.checked, "forLease")
                                                                    }
                                                                    color="primary"
                                                                />
                                                            }
                                                            label="Lease"
                                                        />
                                                    </div>
                                                    <div>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={get(
                                                                        searchCriteria,
                                                                        "listingTypeCriteria.forSale",
                                                                        false
                                                                    )}
                                                                    onChange={(e) =>
                                                                        checkForLeaseOnly(e.target.checked, "forSale")
                                                                    }
                                                                    color="primary"
                                                                />
                                                            }
                                                            label="Sale"
                                                        />
                                                    </div>
                                                </div>

                                                <Divider style={{ margin: "4px -20px 10px -20px" }} />

                                                <div
                                                    style={{
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                    }}
                                                >
                                                    <div>
                                                        <div>
                                                            <FormControlLabel
                                                                control={
                                                                    <Checkbox
                                                                        disabled={get(
                                                                            searchCriteria,
                                                                            "listingTypeCriteria.excludeSubleases",
                                                                            false
                                                                        )}
                                                                        checked={get(
                                                                            searchCriteria,
                                                                            "listingTypeCriteria.forSublease",
                                                                            false
                                                                        )}
                                                                        onChange={(e) =>
                                                                            checkForLeaseOnly(
                                                                                e.target.checked,
                                                                                "forSublease"
                                                                            )
                                                                        }
                                                                        color="primary"
                                                                    />
                                                                }
                                                                label="Sublease Only"
                                                            />
                                                        </div>
                                                        <div>
                                                            <FormControlLabel
                                                                control={
                                                                    <Checkbox
                                                                        checked={get(
                                                                            searchCriteria,
                                                                            "listingTypeCriteria.investmentSaleFlg",
                                                                            false
                                                                        )}
                                                                        onChange={(e) =>
                                                                            checkForLeaseOnly(
                                                                                e.target.checked,
                                                                                "investmentSaleFlg"
                                                                            )
                                                                        }
                                                                        color="primary"
                                                                    />
                                                                }
                                                                label="Investment Only"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div>
                                                            <FormControlLabel
                                                                control={
                                                                    <Checkbox
                                                                        disabled={get(
                                                                            searchCriteria,
                                                                            "listingTypeCriteria.forSublease",
                                                                            false
                                                                        )}
                                                                        checked={get(
                                                                            searchCriteria,
                                                                            "listingTypeCriteria.excludeSubleases",
                                                                            false
                                                                        )}
                                                                        onChange={(e) =>
                                                                            checkForLeaseOnly(
                                                                                e.target.checked,
                                                                                "excludeSubleases"
                                                                            )
                                                                        }
                                                                        color="primary"
                                                                    />
                                                                }
                                                                label="Exclude Subleases"
                                                            />
                                                        </div>

                                                        {canViewExpiredOnly && (
                                                            <div>
                                                                <FormControlLabel
                                                                    control={
                                                                        <Checkbox
                                                                            checked={get(
                                                                                searchCriteria,
                                                                                "listingTypeCriteria.expiredOnly",
                                                                                false
                                                                            )}
                                                                            onChange={(e) =>
                                                                                updateFilter(
                                                                                    "listingTypeCriteria.expiredOnly",
                                                                                    e.target.checked
                                                                                )
                                                                            }
                                                                            color="primary"
                                                                        />
                                                                    }
                                                                    label="Expired Only"
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                <Divider style={{ margin: "10px -20px 18px -20px" }} />

                                                <div
                                                    style={{
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        height: "94px",
                                                        gap: "1vw",
                                                        alignItems: "center",
                                                    }}
                                                >
                                                    <BSInput
                                                        id="minPrice"
                                                        label="Sale Price"
                                                        value={get(searchCriteria, "priceCriteria.minPrice", "")}
                                                        onChange={(e) =>
                                                            updateFilter("priceCriteria.minPrice", e.target.value)
                                                        }
                                                        placeholder="Min Sale price"
                                                        disabled={false}
                                                        onKeyPress={onKeyPress}
                                                        numeric
                                                        extraFormControlStyles={{ flex: "1 1 auto" }}
                                                    />
                                                    <div style={{ color: "grey" }} className="betweenInputs">
                                                        &mdash;
                                                    </div>
                                                    <BSInput
                                                        id="maxPrice"
                                                        value={get(searchCriteria, "priceCriteria.maxPrice", "")}
                                                        onChange={(e) =>
                                                            updateFilter("priceCriteria.maxPrice", e.target.value)
                                                        }
                                                        placeholder="Max Sale price"
                                                        disabled={false}
                                                        onKeyPress={onKeyPress}
                                                        numeric
                                                        extraFormControlStyles={{ flex: "1 1 auto" }}
                                                    />
                                                </div>
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        height: "94px",
                                                        gap: "1vw",
                                                        alignItems: "center",
                                                    }}
                                                >
                                                    <Grid item xs={4}>
                                                        <BSInput
                                                            id="minLeaseRate"
                                                            label="Lease Rate"
                                                            placeholder="Min Lease Rate"
                                                            value={get(
                                                                searchCriteria,
                                                                "priceCriteria.minLeaseRate",
                                                                ""
                                                            )}
                                                            onChange={(e) =>
                                                                updateFilter(
                                                                    "priceCriteria.minLeaseRate",
                                                                    e.target.value
                                                                )
                                                            }
                                                            onKeyPress={onKeyPress}
                                                            numeric
                                                        />
                                                    </Grid>
                                                    <Grid item align="center">
                                                        <div className="betweenInputs" style={{ color: "grey" }}>
                                                            &mdash;
                                                        </div>
                                                    </Grid>
                                                    <Grid item xs={4}>
                                                        <BSInput
                                                            id="maxLeaseRate"
                                                            placeholder="Max Lease Rate"
                                                            value={get(
                                                                searchCriteria,
                                                                "priceCriteria.maxLeaseRate",
                                                                ""
                                                            )}
                                                            onChange={(e) =>
                                                                updateFilter(
                                                                    "priceCriteria.maxLeaseRate",
                                                                    e.target.value
                                                                )
                                                            }
                                                            onKeyPress={onKeyPress}
                                                            numeric
                                                        />
                                                    </Grid>
                                                    <Grid item xs={3}>
                                                        <BSSelect
                                                            id="leaseRateType"
                                                            value={get(
                                                                searchCriteria,
                                                                "priceCriteria.leaseRateType",
                                                                ""
                                                            )}
                                                            onChange={(e) =>
                                                                updateFilter(
                                                                    "priceCriteria.leaseRateType",
                                                                    e.target.value
                                                                )
                                                            }
                                                            placeholder="Select"
                                                            options={[
                                                                {
                                                                    value: "$/SF/Month",
                                                                    label: "$/SF/Month",
                                                                },
                                                                {
                                                                    value: "$/SF/Year",
                                                                    label: "$/SF/Year",
                                                                },
                                                            ]}
                                                            disabled={false}
                                                        />
                                                    </Grid>
                                                </div>

                                                <BSInput
                                                    label="Available On Or Before"
                                                    type={"date"}
                                                    placeholder="Available Before Date"
                                                    value={get(
                                                        searchCriteria,
                                                        "listingTypeCriteria.availableBefore",
                                                        ""
                                                    )}
                                                    onChange={(e) =>
                                                        updateFilter(
                                                            "listingTypeCriteria.availableBefore",
                                                            e.target.value
                                                        )
                                                    }
                                                />

                                                <div
                                                    style={{
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        height: "94px",
                                                        gap: "1vw",
                                                        alignItems: "center",
                                                    }}
                                                >
                                                    <BSInput
                                                        label="Date Listed"
                                                        placeholder="Date Listed (From)"
                                                        value={get(
                                                            searchCriteria,
                                                            "listingTypeCriteria.dateListedFrom",
                                                            ""
                                                        )}
                                                        type={"date"}
                                                        onChange={(e) =>
                                                            updateFilter(
                                                                "listingTypeCriteria.dateListedFrom",
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                    <div className="betweenInputs" style={{ color: "grey" }}>
                                                        &mdash;
                                                    </div>
                                                    <BSInput
                                                        placeholder="Date Listed (To)"
                                                        value={get(
                                                            searchCriteria,
                                                            "listingTypeCriteria.dateListedTo",
                                                            ""
                                                        )}
                                                        type={"date"}
                                                        onChange={(e) =>
                                                            updateFilter(
                                                                "listingTypeCriteria.dateListedTo",
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </div>

                                                <Grid container justifyContent="flex-end">
                                                    <Grid item>
                                                        <MenuSearchButton
                                                            variant="contained"
                                                            color="primary"
                                                            onClick={handleSearchSubmit}
                                                        >
                                                            Search
                                                        </MenuSearchButton>
                                                    </Grid>
                                                </Grid>
                                            </Element>
                                        </ListingTypeFilterContainer>
                                    </ClickAwayListener>
                                </div>
                            </Fade>
                        )}
                    </Popper>
                    <FakeSelect onClick={(e) => handleClick("bottom-start")(e, "dealType")}>
                        {`Type & Price ${
                            countFilter(searchCriteria, "listingTypeCriteria") > 0
                                ? `(${countFilter(searchCriteria, "listingTypeCriteria")})`
                                : ""
                        }`}
                        <FakeSelectIcon>
                            <DownChevron />
                        </FakeSelectIcon>
                    </FakeSelect>
                </StyledFilterDropdownMenu>

                <StyledFilterDropdownMenu>
                    <Popper open={openDropdown === "status"} anchorEl={anchorEl} placement={placement} transition>
                        {({ TransitionProps }) => (
                            <Fade {...TransitionProps} timeout={100}>
                                <div>
                                    <ClickAwayListener onClickAway={handleClose}>
                                        <StatusContainer>
                                            <Grid container justifyContent="space-between" alignItems="center">
                                                <div>
                                                    <MenuTitle>KM Managed Only</MenuTitle>
                                                </div>
                                                <StyledDivider />

                                                <div>
                                                    <StatusSwitch
                                                        checked={get(
                                                            searchCriteria,
                                                            "statusCriteria.kmManagedOnly",
                                                            false
                                                        )}
                                                        onChange={(e) =>
                                                            updateFilter(
                                                                "statusCriteria.kmManagedOnly",
                                                                e.target.checked
                                                            )
                                                        }
                                                        value="kmManagedOnly"
                                                        color="primary"
                                                    />
                                                </div>
                                            </Grid>
                                            <div>
                                                <MenuSectionTitle>Status </MenuSectionTitle>
                                            </div>
                                            <StyledStatusFilterContainer>
                                                <div>
                                                    <div>
                                                        <FormControlLabel
                                                            value="existing"
                                                            control={
                                                                <Checkbox
                                                                    color="primary"
                                                                    checked={get(
                                                                        searchCriteria,
                                                                        "statusCriteria.existing",
                                                                        false
                                                                    )}
                                                                    onClick={(e) =>
                                                                        updateFilter(
                                                                            "statusCriteria.existing",
                                                                            e.target.checked
                                                                        )
                                                                    }
                                                                />
                                                            }
                                                            label="Existing"
                                                            labelPlacement="end"
                                                        />
                                                    </div>
                                                    <div>
                                                        <FormControlLabel
                                                            value="underConstruction"
                                                            control={
                                                                <Checkbox
                                                                    color="primary"
                                                                    checked={get(
                                                                        searchCriteria,
                                                                        "statusCriteria.underConstruction",
                                                                        false
                                                                    )}
                                                                    onClick={(e) =>
                                                                        updateFilter(
                                                                            "statusCriteria.underConstruction",
                                                                            e.target.checked
                                                                        )
                                                                    }
                                                                />
                                                            }
                                                            label="Under Construction"
                                                            labelPlacement="end"
                                                        />
                                                    </div>
                                                    <div>
                                                        <FormControlLabel
                                                            value="demolished"
                                                            control={
                                                                <Checkbox
                                                                    color="primary"
                                                                    checked={get(
                                                                        searchCriteria,
                                                                        "statusCriteria.demolished",
                                                                        false
                                                                    )}
                                                                    onClick={(e) =>
                                                                        updateFilter(
                                                                            "statusCriteria.demolished",
                                                                            e.target.checked
                                                                        )
                                                                    }
                                                                />
                                                            }
                                                            label="Demolished"
                                                            labelPlacement="end"
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <div>
                                                        <FormControlLabel
                                                            value="proposed"
                                                            control={
                                                                <Checkbox
                                                                    color="primary"
                                                                    checked={get(
                                                                        searchCriteria,
                                                                        "statusCriteria.proposed",
                                                                        false
                                                                    )}
                                                                    onClick={(e) =>
                                                                        updateFilter(
                                                                            "statusCriteria.proposed",
                                                                            e.target.checked
                                                                        )
                                                                    }
                                                                />
                                                            }
                                                            label="Proposed"
                                                            labelPlacement="end"
                                                        />
                                                    </div>
                                                    <div>
                                                        <FormControlLabel
                                                            value="underRenovation"
                                                            control={
                                                                <Checkbox
                                                                    color="primary"
                                                                    checked={get(
                                                                        searchCriteria,
                                                                        "statusCriteria.underRenovation",
                                                                        false
                                                                    )}
                                                                    onClick={(e) =>
                                                                        updateFilter(
                                                                            "statusCriteria.underRenovation",
                                                                            e.target.checked
                                                                        )
                                                                    }
                                                                />
                                                            }
                                                            label="Under Renovation"
                                                            labelPlacement="end"
                                                        />
                                                    </div>
                                                    <div>
                                                        <FormControlLabel
                                                            value="land"
                                                            control={
                                                                <Checkbox
                                                                    color="primary"
                                                                    checked={get(
                                                                        searchCriteria,
                                                                        "statusCriteria.land",
                                                                        false
                                                                    )}
                                                                    onClick={(e) =>
                                                                        updateFilter(
                                                                            "statusCriteria.land",
                                                                            e.target.checked
                                                                        )
                                                                    }
                                                                />
                                                            }
                                                            label="Land"
                                                            labelPlacement="end"
                                                        />
                                                    </div>
                                                </div>
                                            </StyledStatusFilterContainer>
                                            <MenuSectionTitle>Tenancy </MenuSectionTitle>
                                            <div style={{ display: "flex" }}>
                                                <div>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={get(
                                                                    searchCriteria,
                                                                    "tenancyCriteria.single",
                                                                    false
                                                                )}
                                                                onChange={(e) =>
                                                                    updateFilter(
                                                                        "tenancyCriteria.single",
                                                                        e.target.checked
                                                                    )
                                                                }
                                                                color="primary"
                                                            />
                                                        }
                                                        label="Single"
                                                    />
                                                </div>
                                                <div>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={get(
                                                                    searchCriteria,
                                                                    "tenancyCriteria.multi",
                                                                    false
                                                                )}
                                                                onChange={(e) =>
                                                                    updateFilter(
                                                                        "tenancyCriteria.multi",
                                                                        e.target.checked
                                                                    )
                                                                }
                                                                color="primary"
                                                            />
                                                        }
                                                        label="Multi"
                                                    />
                                                </div>
                                            </div>
                                            <Grid container justifyContent="flex-end">
                                                <Grid item>
                                                    <MenuSearchButton
                                                        variant="contained"
                                                        color="primary"
                                                        onClick={handleSearchSubmit}
                                                    >
                                                        Search
                                                    </MenuSearchButton>
                                                </Grid>
                                            </Grid>
                                        </StatusContainer>
                                    </ClickAwayListener>
                                </div>
                            </Fade>
                        )}
                    </Popper>
                    <FakeSelect onClick={(e) => handleClick("bottom-start")(e, "status")}>
                        {`Status ${
                            combineFilterCount(searchCriteria, "statusCriteria", "tenancyCriteria") > 0
                                ? `(${combineFilterCount(searchCriteria, "statusCriteria", "tenancyCriteria")})`
                                : ""
                        }`}
                        <FakeSelectIcon>
                            <DownChevron />
                        </FakeSelectIcon>
                    </FakeSelect>
                </StyledFilterDropdownMenu>

                <StyledFilterDropdownMenu>
                    <Popper open={openDropdown === "size"} anchorEl={anchorEl} placement={placement} transition>
                        {({ TransitionProps }) => (
                            <Fade {...TransitionProps} timeout={100}>
                                <div>
                                    <ClickAwayListener onClickAway={handleClose}>
                                        <StatusContainer>
                                            <div
                                                style={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                }}
                                            >
                                                <div>
                                                    <FormControlLabel
                                                        value="availableSf"
                                                        control={
                                                            <Radio
                                                                color="primary"
                                                                checked={get(
                                                                    searchCriteria,
                                                                    "sizeCriteria.availableSf",
                                                                    true
                                                                )}
                                                                onClick={() =>
                                                                    handleSizeRadioClick(
                                                                        "availableSf",
                                                                        !get(
                                                                            searchCriteria,
                                                                            "sizeCriteria.availableSf",
                                                                            false
                                                                        )
                                                                    )
                                                                }
                                                            />
                                                        }
                                                        label="Lease/Sale SF"
                                                    />
                                                </div>
                                                <div>
                                                    <FormControlLabel
                                                        value="totalSf"
                                                        control={
                                                            <Radio
                                                                color="primary"
                                                                checked={get(
                                                                    searchCriteria,
                                                                    "sizeCriteria.totalSf",
                                                                    false
                                                                )}
                                                                onClick={(e) =>
                                                                    handleSizeRadioClick(
                                                                        "totalSf",
                                                                        !get(
                                                                            searchCriteria,
                                                                            "sizeCriteria.totalSf",
                                                                            false
                                                                        )
                                                                    )
                                                                }
                                                            />
                                                        }
                                                        label="Total SF"
                                                    />
                                                </div>
                                                <div>
                                                    <FormControlLabel
                                                        value="acreage"
                                                        control={
                                                            <Radio
                                                                color="primary"
                                                                checked={get(
                                                                    searchCriteria,
                                                                    "sizeCriteria.acreage",
                                                                    false
                                                                )}
                                                                onClick={(e) =>
                                                                    handleSizeRadioClick(
                                                                        "acreage",
                                                                        !get(
                                                                            searchCriteria,
                                                                            "sizeCriteria.acreage",
                                                                            false
                                                                        )
                                                                    )
                                                                }
                                                            />
                                                        }
                                                        label="Total Acreage"
                                                    />
                                                </div>
                                            </div>

                                            <div style={{ display: "flex", alignItems: "center", gap: "1vw" }}>
                                                <BSInput
                                                    placeholder="Min SF"
                                                    value={get(searchCriteria, "sizeCriteria.min", "")}
                                                    onChange={(e) => updateFilter("sizeCriteria.min", e.target.value)}
                                                    onKeyPress={onKeyPress}
                                                    numeric
                                                />

                                                <div>&mdash;</div>

                                                <BSInput
                                                    placeholder="Max SF"
                                                    value={get(searchCriteria, "sizeCriteria.max", "")}
                                                    onChange={(e) => updateFilter("sizeCriteria.max", e.target.value)}
                                                    onKeyPress={onKeyPress}
                                                    numeric
                                                />
                                            </div>

                                            <Grid container spacing={16} justifyContent="flex-end">
                                                <Grid item>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        onClick={handleSearchSubmit}
                                                    >
                                                        Search
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </StatusContainer>
                                    </ClickAwayListener>
                                </div>
                            </Fade>
                        )}
                    </Popper>
                    <FakeSelect onClick={(e) => handleClick("bottom-start")(e, "size")}>
                        Size
                        <FakeSelectIcon>
                            <DownChevron />
                        </FakeSelectIcon>
                    </FakeSelect>
                </StyledFilterDropdownMenu>

                <StyledFilterDropdownMenu>
                    <Popper open={openDropdown === "kmOffice"} anchorEl={anchorEl} placement={placement} transition>
                        {({ TransitionProps }) => (
                            <Fade {...TransitionProps} timeout={100}>
                                <div>
                                    <ClickAwayListener onClickAway={handleClose}>
                                        <StatusContainer>
                                            <Element style={{ height: "400px", overflowY: "scroll" }}>
                                                <Grid container direction="column">
                                                    {/*for each office*/}
                                                    {Object.keys(searchConstants.activeKmOffices).map((key, i) => {
                                                        const option = searchConstants.activeKmOffices[key];
                                                        return (
                                                            <OfficeDropdown item>
                                                                <FormControlLabel
                                                                    control={
                                                                        <Checkbox
                                                                            checked={get(
                                                                                searchCriteria,
                                                                                `officeCriteria.${key}`,
                                                                                false
                                                                            )}
                                                                            onChange={(e) =>
                                                                                updateFilter(
                                                                                    "officeCriteria." + key,
                                                                                    e.target.checked
                                                                                )
                                                                            }
                                                                            color="primary"
                                                                        />
                                                                    }
                                                                    label={option.display}
                                                                />
                                                            </OfficeDropdown>
                                                        );
                                                    })}
                                                </Grid>
                                            </Element>
                                            <Grid container spacing={16} justifyContent="flex-end">
                                                <Grid item style={{ paddingTop: "30px" }}>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        onClick={handleSearchSubmit}
                                                    >
                                                        Search
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </StatusContainer>
                                    </ClickAwayListener>
                                </div>
                            </Fade>
                        )}
                    </Popper>
                    <FakeSelect onClick={(e) => handleClick("bottom-start")(e, "kmOffice")}>
                        {`KM Office ${
                            countFilter(searchCriteria, "officeCriteria") > 0
                                ? `(${countFilter(searchCriteria, "officeCriteria")})`
                                : ""
                        }`}
                        <FakeSelectIcon>
                            <DownChevron />
                        </FakeSelectIcon>
                    </FakeSelect>
                </StyledFilterDropdownMenu>

                <div>
                    <Button variant="outlined" onClick={handleClearFilters}>
                        Clear
                    </Button>
                </div>
            </StyledFilterContainer>

            <Grid
                container
                justifyContent="space-between"
                alignItems="center"
                spacing={8}
                style={{ marginTop: "10px" }}
            >
                <Grid item>
                    {loadedSearchCrumbs
                        ? Object.keys(loadedSearchCrumbs).map((criteria) => {
                              if (loadedSearchCrumbs[criteria].value === true) {
                                  if (Object.keys(loadedSearchCrumbs[criteria]).length === 2) {
                                      return (
                                          <Chip
                                              style={{ marginLeft: "10px", marginBottom: "10px" }}
                                              color="primary"
                                              label={criteria}
                                              onDelete={() => handleDeleteCrumb(loadedSearchCrumbs[criteria], false)}
                                          />
                                      );
                                  } else if (
                                      (loadedSearchCrumbs[criteria].valueMin !== "* " &&
                                          loadedSearchCrumbs[criteria].valueMin !== undefined) ||
                                      (loadedSearchCrumbs[criteria].valueMax !== " *" &&
                                          loadedSearchCrumbs[criteria].valueMax !== undefined)
                                  ) {
                                      return (
                                          <Chip
                                              style={{ marginLeft: "10px", marginBottom: "10px" }}
                                              color="primary"
                                              label={
                                                  criteria +
                                                  get(loadedSearchCrumbs[criteria], "unit", "") +
                                                  ": " +
                                                  get(loadedSearchCrumbs[criteria], "valueMin", "* ") +
                                                  "-" +
                                                  get(loadedSearchCrumbs[criteria], "valueMax", " *")
                                              }
                                              onDelete={() => handleDeleteCrumb(loadedSearchCrumbs[criteria], null)}
                                          />
                                      );
                                  } else {
                                      return null;
                                  }
                              } else if (
                                  typeof loadedSearchCrumbs[criteria].value == "string" &&
                                  loadedSearchCrumbs[criteria].value != null
                              ) {
                                  return (
                                      <Chip
                                          style={{ marginLeft: "10px", marginBottom: "10px" }}
                                          color="primary"
                                          label={criteria + ": " + loadedSearchCrumbs[criteria].value}
                                          onDelete={() => handleDeleteCrumb(loadedSearchCrumbs[criteria], null)}
                                      />
                                  );
                              } else {
                                  return null;
                              }
                          })
                        : ""}
                </Grid>
            </Grid>
        </FilterBar>
    );
};

export default ListingFilters;
